// Voice notes on the alert form.
//
// Typing a defect description on a phone with gloves on is the slowest part of
// raising an alert, so the spec offers dictation. This uses the browser's own
// speech recognition, which on Android Chrome means the audio goes to Google's
// servers — that is a real disclosure, not a formality, which is why the first
// use asks and the answer is remembered.
//
// The transcript is only ever a draft: it lands in the notes field where the
// operator can correct it before anything is submitted. Recognition is wrong
// often enough with machinery running that treating it as final would put
// invented words into a permanent quality record.

import { useCallback, useEffect, useRef, useState } from 'react';
import { speechLocale, type Lang } from '@/i18n/strings';

type Recognition = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
};

function recognitionCtor(): (new () => Recognition) | null {
  if (import.meta.env['VITE_DISABLE_SPEECH'] === 'true') return null;
  const w = window as unknown as Record<string, unknown>;
  return (w['SpeechRecognition'] ?? w['webkitSpeechRecognition']) as (new () => Recognition) | null;
}

export interface Dictation {
  /** False when the browser has no speech recognition, or it is switched off. */
  supported: boolean;
  listening: boolean;
  /** What has been heard so far this session, including the interim guess. */
  transcript: string;
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useDictation(lang: Lang): Dictation {
  const [supported] = useState(() => recognitionCtor() != null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<Recognition | null>(null);
  const settled = useRef('');

  const stop = useCallback(() => {
    ref.current?.stop();
    setListening(false);
  }, []);

  // Never leave the microphone open because a sheet closed.
  useEffect(() => () => ref.current?.stop(), []);

  const start = useCallback(() => {
    const Ctor = recognitionCtor();
    if (!Ctor) return;
    setError(null);
    settled.current = '';
    setTranscript('');

    const rec = new Ctor();
    ref.current = rec;
    rec.lang = speechLocale(lang);
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const alt = e.results[i]?.[0];
        if (!alt) continue;
        if (e.results[i]?.isFinal) settled.current += alt.transcript;
        else interim += alt.transcript;
      }
      setTranscript((settled.current + interim).trim());
    };
    rec.onerror = (e) => {
      // "not-allowed" is the operator declining the microphone at the OS
      // prompt, which needs different words from a genuine failure.
      setError(
        e.error === 'not-allowed' || e.error === 'service-not-allowed'
          ? 'Microphone access was declined. Type the note instead.'
          : 'Dictation stopped. Type the note instead.',
      );
      setListening(false);
    };
    rec.onend = () => setListening(false);

    try {
      rec.start();
      setListening(true);
    } catch {
      setError('Dictation could not start. Type the note instead.');
    }
  }, [lang]);

  return { supported, listening, transcript, error, start, stop };
}
